import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { partnersApi, dealFlowApi, investorApi } from '../lib/api';
import { DealContractEditor } from '../components/contracts/DealContractEditor';
import { toast } from 'sonner';
import {
  Users, Plus, Search, UserCheck, UserPlus, DollarSign,
  Building2, Phone, Mail, FileText, Upload, Download,
  Trash2, Edit2, ChevronRight, Shield, Inbox,
  CheckCircle2, XCircle, Clock, Send, Handshake, Eye, Copy, Link2,
  Loader2, TrendingUp, Percent, ArrowUpRight, MapPin
} from 'lucide-react';

const fmt = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(v || 0);

export function Partners() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [minCapital, setMinCapital] = useState('');
  const [maxCapital, setMaxCapital] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editPartner, setEditPartner] = useState(null);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [promoting, setPromoting] = useState(null);
  const [confirmPromote, setConfirmPromote] = useState(null);
  const [activeTab, setActiveTab] = useState('partners');
  const [applications, setApplications] = useState([]);
  const [appsLoading, setAppsLoading] = useState(false);
  const [promotedDeals, setPromotedDeals] = useState([]);
  const [contractDeal, setContractDeal] = useState(null);
  const [termsDeal, setTermsDeal] = useState(null);

  const handleSetDealType = async (deal, newType) => {
    try {
      await dealFlowApi.setDealType(deal.id, newType || null);
      toast.success(newType ? `Marked as ${newType}` : 'Deal type cleared');
      await loadApplications();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update deal type');
    }
  };

  const loadPartners = useCallback(async () => {
    try {
      const res = await partnersApi.list(statusFilter !== 'all' ? statusFilter : undefined);
      setPartners(res.data);
    } catch (err) {
      toast.error('Failed to load partners');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const loadApplications = useCallback(async () => {
    setAppsLoading(true);
    try {
      const [appsRes, dealsRes] = await Promise.all([
        dealFlowApi.listApplications({}),
        dealFlowApi.listPromoted(),
      ]);
      setApplications(appsRes.data || []);
      setPromotedDeals(dealsRes.data || []);
    } catch (err) {
      // Non-blocking: applications/promoted deals are secondary panels.
      console.error('Failed to load deal-flow data:', err);
    } finally {
      setAppsLoading(false);
    }
  }, []);

  useEffect(() => { loadPartners(); }, [loadPartners]);
  useEffect(() => {
    if (activeTab === 'applications' || activeTab === 'promoted') {
      loadApplications();
    }
  }, [activeTab, loadApplications]);

  const filtered = partners.filter(p => {
    // Text search
    if (search) {
      const s = search.toLowerCase();
      if (!(p.name?.toLowerCase().includes(s) || p.entity_name?.toLowerCase().includes(s) || p.email?.toLowerCase().includes(s))) return false;
    }
    // Capital range
    const cap = p.total_available_capital || 0;
    const min = parseFloat(String(minCapital).replace(/[,$]/g, ''));
    const max = parseFloat(String(maxCapital).replace(/[,$]/g, ''));
    if (!isNaN(min) && min > 0 && cap < min) return false;
    if (!isNaN(max) && max > 0 && cap > max) return false;
    return true;
  });

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this partner?')) return;
    try {
      await partnersApi.delete(id);
      toast.success('Partner deleted');
      loadPartners();
      if (selectedPartner?.id === id) setSelectedPartner(null);
    } catch (err) {
      toast.error('Failed to delete partner');
    }
  };

  const handlePromote = async (partner) => {
    setPromoting(partner.id);
    try {
      const res = await partnersApi.promote(partner.id);
      toast.success(`${partner.name} promoted to active partner!`);
      setConfirmPromote(null);
      loadPartners();
      if (selectedPartner?.id === partner.id) setSelectedPartner(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to promote partner');
    } finally {
      setPromoting(null);
    }
  };

  const counts = {
    all: partners.length,
    prospect: partners.filter(p => p.status === 'prospect').length,
    active: partners.filter(p => p.status === 'active').length,
  };

  const pendingApps = applications.filter(a => a.status === 'applied');

  const handleApproveApp = async (appId) => {
    try {
      await dealFlowApi.approveApplication(appId);
      toast.success('Application approved — partner will see full details');
      loadApplications();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleDeclineApp = async (appId) => {
    if (!window.confirm('Decline this application?')) return;
    try {
      await dealFlowApi.declineApplication(appId);
      toast.success('Application declined');
      loadApplications();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to decline');
    }
  };

  const appStatusBadge = (status) => {
    const map = {
      applied: { label: 'Pending', cls: 'bg-amber-100 text-amber-800' },
      approved: { label: 'Approved', cls: 'bg-emerald-100 text-emerald-800' },
      accepted: { label: 'Accepted', cls: 'bg-green-100 text-green-800' },
      declined_by_admin: { label: 'Declined', cls: 'bg-red-100 text-red-700' },
      declined_by_partner: { label: 'Partner Declined', cls: 'bg-gray-100 text-gray-600' },
      contract_sent: { label: 'Contract Sent', cls: 'bg-purple-100 text-purple-800' },
    };
    const m = map[status] || { label: status, cls: 'bg-gray-100 text-gray-600' };
    return <Badge className={`text-[10px] ${m.cls}`}>{m.label}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="partners-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-heading font-bold">Partners & JV Management</h1>
          <p className="text-xs sm:text-sm text-muted-foreground">
            Manage investor partners, track capital, and process JV deal commitments
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="rounded-sm gap-1.5 text-xs"
            onClick={() => {
              navigator.clipboard.writeText('https://landflow.wefindland.com/partner');
              toast.success('Partner intake link copied!');
            }}
            data-testid="copy-partner-link-btn"
          >
            <Link2 className="w-3.5 h-3.5" /> Copy Intake Link
          </Button>
          <Button
            onClick={() => { setEditPartner(null); setShowForm(true); }}
            className="bg-[#1A2F23] hover:bg-[#1A2F23]/90 text-white rounded-sm gap-1.5"
            data-testid="add-partner-btn"
          >
            <Plus className="w-4 h-4" /> Add Partner
          </Button>
        </div>
      </div>

      {/* Tabs: Partners & Applications */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="partners" data-testid="tab-partners">
            <Users className="w-4 h-4 mr-1.5" /> Partners
          </TabsTrigger>
          <TabsTrigger value="applications" className="relative" data-testid="tab-applications">
            <Inbox className="w-4 h-4 mr-1.5" /> Applications
            {pendingApps.length > 0 && (
              <span className="ml-1.5 w-5 h-5 rounded-full bg-amber-500 text-white text-[10px] font-bold flex items-center justify-center">
                {pendingApps.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="promoted" data-testid="tab-promoted-deals">
            <Send className="w-4 h-4 mr-1.5" /> Promoted Deals
          </TabsTrigger>
        </TabsList>

        {/* Partners Tab */}
        <TabsContent value="partners" className="space-y-6 mt-4">

      {/* Stats Row */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Total Partners', value: counts.all, icon: Users },
          { label: 'Prospects', value: counts.prospect, icon: UserPlus },
          { label: 'Active Partners', value: counts.active, icon: UserCheck },
        ].map(s => (
          <Card key={s.label} className="rounded-sm border">
            <CardContent className="py-4 px-5 flex items-center gap-3">
              <div className="w-9 h-9 rounded-sm bg-muted/50 flex items-center justify-center">
                <s.icon className="w-4 h-4 text-muted-foreground" />
              </div>
              <div>
                <div className="text-lg font-bold">{s.value}</div>
                <div className="text-[11px] text-muted-foreground uppercase tracking-wide">{s.label}</div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, entity, or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-9 rounded-sm"
            data-testid="partner-search"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 h-9 rounded-sm" data-testid="partner-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All ({counts.all})</SelectItem>
            <SelectItem value="prospect">Prospects ({counts.prospect})</SelectItem>
            <SelectItem value="active">Active ({counts.active})</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1.5">
          <DollarSign className="w-4 h-4 text-muted-foreground flex-shrink-0" />
          <Input
            placeholder="Min"
            value={minCapital}
            onChange={(e) => setMinCapital(e.target.value)}
            className="h-9 w-28 rounded-sm font-mono text-sm"
            data-testid="partner-min-capital"
          />
          <span className="text-xs text-muted-foreground">–</span>
          <Input
            placeholder="Max"
            value={maxCapital}
            onChange={(e) => setMaxCapital(e.target.value)}
            className="h-9 w-28 rounded-sm font-mono text-sm"
            data-testid="partner-max-capital"
          />
        </div>
        {(search || statusFilter !== 'all' || minCapital || maxCapital) && (
          <Button
            variant="ghost"
            size="sm"
            className="h-9 text-xs text-muted-foreground hover:text-foreground"
            onClick={() => { setSearch(''); setStatusFilter('all'); setMinCapital(''); setMaxCapital(''); }}
            data-testid="clear-filters-btn"
          >
            Clear filters
          </Button>
        )}
      </div>
      <div className="text-xs text-muted-foreground">
        Showing {filtered.length} of {partners.length} partners
      </div>

      {/* Partners Table */}
      <Card className="rounded-sm border" data-testid="partners-table">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-muted/30">
                  <th className="text-left p-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Partner</th>
                  <th className="text-left p-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Entity</th>
                  <th className="text-left p-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Contact</th>
                  <th className="text-right p-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Available Capital</th>
                  <th className="text-center p-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Status</th>
                  <th className="text-center p-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">POF</th>
                  <th className="text-right p-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">Loading...</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">No partners found</td></tr>
                ) : filtered.map(p => (
                  <tr
                    key={p.id}
                    className="border-b border-border/50 hover:bg-muted/20 transition-colors cursor-pointer"
                    onClick={() => setSelectedPartner(p)}
                    data-testid={`partner-row-${p.id}`}
                  >
                    <td className="p-3">
                      <div className="font-medium text-sm">{p.name}</div>
                    </td>
                    <td className="p-3 text-sm text-muted-foreground">{p.entity_name || '—'}</td>
                    <td className="p-3">
                      <div className="text-xs text-muted-foreground">{p.email}</div>
                      {p.phone && <div className="text-xs text-muted-foreground">{p.phone}</div>}
                    </td>
                    <td className="p-3 text-right font-mono text-sm">{fmt(p.total_available_capital)}</td>
                    <td className="p-3 text-center">
                      <Badge variant={p.status === 'active' ? 'default' : 'secondary'} className={`text-[10px] ${p.status === 'active' ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100' : ''}`}>
                        {p.status === 'active' ? 'Active' : 'Prospect'}
                      </Badge>
                    </td>
                    <td className="p-3 text-center">
                      {p.proof_of_funds_url ? (
                        <FileText className="w-4 h-4 text-emerald-600 mx-auto" />
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="p-3 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        {p.status === 'prospect' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs gap-1 text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50"
                            onClick={() => setConfirmPromote(p)}
                            disabled={promoting === p.id}
                            data-testid={`promote-btn-${p.id}`}
                          >
                            <Shield className="w-3 h-3" /> {promoting === p.id ? 'Promoting...' : 'Promote'}
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditPartner(p); setShowForm(true); }}>
                          <Edit2 className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-700" onClick={() => handleDelete(p.id)}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Partner Form Dialog */}
      <PartnerFormDialog
        open={showForm}
        onOpenChange={setShowForm}
        partner={editPartner}
        onSaved={() => { setShowForm(false); loadPartners(); }}
      />

      {/* Partner Detail Drawer */}
      <PartnerDetailDialog
        partner={selectedPartner}
        onClose={() => setSelectedPartner(null)}
        onPromote={(p) => { setSelectedPartner(null); setConfirmPromote(p); }}
        promoting={promoting}
        onRefresh={loadPartners}
      />

        </TabsContent>

        {/* Applications Tab */}
        <TabsContent value="applications" className="space-y-4 mt-4">
          {appsLoading ? (
            <div className="text-center py-8 text-muted-foreground">Loading applications...</div>
          ) : applications.length === 0 ? (
            <Card className="rounded-sm border">
              <CardContent className="py-12 text-center">
                <Inbox className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">No deal applications yet.</p>
                <p className="text-xs text-muted-foreground mt-1">Applications will appear here when partners apply for promoted deals.</p>
              </CardContent>
            </Card>
          ) : (
            <Card className="rounded-sm border" data-testid="applications-table">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-muted/30">
                        <th className="text-left p-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Partner</th>
                        <th className="text-left p-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Deal</th>
                        <th className="text-center p-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Status</th>
                        <th className="text-left p-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Applied</th>
                        <th className="text-right p-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {applications.map(app => (
                        <tr key={app.id} className="border-b border-border/50 hover:bg-muted/20" data-testid={`app-row-${app.id}`}>
                          <td className="p-3">
                            <div className="text-sm font-medium">{app.partner_name}</div>
                            <div className="text-[11px] text-muted-foreground">{app.partner_entity || app.partner_email}</div>
                          </td>
                          <td className="p-3">
                            <div className="text-sm">{app.deal_summary?.project_name || app.deal_summary?.proposal_name || '—'}</div>
                            {app.deal_summary?.purchase_price && (
                              <div className="text-[11px] text-muted-foreground font-mono">{fmt(app.deal_summary.purchase_price)}</div>
                            )}
                          </td>
                          <td className="p-3 text-center">{appStatusBadge(app.status)}</td>
                          <td className="p-3 text-xs text-muted-foreground">
                            {app.applied_at ? new Date(app.applied_at).toLocaleDateString() : '—'}
                          </td>
                          <td className="p-3 text-right">
                            {app.status === 'applied' && (
                              <div className="flex items-center justify-end gap-1.5">
                                <Button
                                  size="sm"
                                  className="h-7 text-xs gap-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm"
                                  onClick={() => handleApproveApp(app.id)}
                                  data-testid={`approve-app-${app.id}`}
                                >
                                  <CheckCircle2 className="w-3 h-3" /> Approve
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 text-xs gap-1 text-red-600 border-red-200 hover:bg-red-50 rounded-sm"
                                  onClick={() => handleDeclineApp(app.id)}
                                  data-testid={`decline-app-${app.id}`}
                                >
                                  <XCircle className="w-3 h-3" /> Decline
                                </Button>
                              </div>
                            )}
                            {app.status === 'approved' && (
                              <span className="text-[11px] text-emerald-600">Awaiting partner decision</span>
                            )}
                            {app.status === 'accepted' && (
                              <span className="text-[11px] text-green-600 flex items-center justify-end gap-1"><CheckCircle2 className="w-3 h-3" /> Committed</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Promoted Deals Tab */}
        <TabsContent value="promoted" className="space-y-4 mt-4">
          {promotedDeals.length === 0 ? (
            <Card className="rounded-sm border">
              <CardContent className="py-12 text-center">
                <Send className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">No promoted deals yet.</p>
                <p className="text-xs text-muted-foreground mt-1">Promote proposals from the Proposals page to send them to partners.</p>
              </CardContent>
            </Card>
          ) : promotedDeals.map(deal => (
            <Card key={deal.id} className="rounded-sm border" data-testid={`promoted-deal-${deal.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h3 className="text-sm font-semibold">{deal.summary?.project_name || deal.summary?.proposal_name}</h3>
                      <Badge variant={deal.status === 'open' ? 'default' : deal.status === 'committed' ? 'secondary' : 'outline'}
                        className={`text-[10px] ${deal.status === 'open' ? 'bg-emerald-100 text-emerald-800' : deal.status === 'committed' ? 'bg-green-100 text-green-800' : ''}`}
                      >
                        {deal.status === 'open' ? 'Open' : deal.status === 'committed' ? 'Committed' : deal.status}
                      </Badge>
                      {deal.deal_type && (
                        <Select
                          value={deal.deal_type}
                          onValueChange={(v) => handleSetDealType(deal, v)}
                        >
                          <SelectTrigger
                            className={`h-6 px-2 py-0 text-[10px] font-bold uppercase tracking-wide rounded-sm border w-auto gap-1 ${deal.deal_type === 'subdivide' ? 'border-blue-300 text-blue-700 bg-blue-50' : 'border-orange-300 text-orange-700 bg-orange-50'}`}
                            data-testid={`deal-type-select-${deal.id}`}
                            title={deal.deal_type_override ? 'Manually set' : 'Auto-detected — click to override'}
                          >
                            <SelectValue>
                              {deal.deal_type}
                              {deal.deal_type_override && <span className="ml-1 text-[8px] opacity-60">●</span>}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="flip">Flip</SelectItem>
                            <SelectItem value="subdivide">Subdivide</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                    <div className="flex gap-4 text-xs text-muted-foreground">
                      <span>Purchase: {fmt(deal.summary?.purchase_price)}</span>
                      <span>Capital: {fmt(deal.summary?.total_investor_capital)}</span>
                      <span>ROI: {deal.summary?.projected_roi?.toFixed(1)}%</span>
                    </div>
                    {deal.created_by && (
                      <div className="text-[11px] text-muted-foreground/80 mt-1.5">
                        Promoted by <span className="font-medium text-foreground/70">{deal.created_by}</span>
                        {deal.created_at && (
                          <span> · {new Date(deal.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="text-right text-xs text-muted-foreground">
                    <div>{deal.partner_ids?.length || 0} partners invited</div>
                    <div className="flex items-center gap-2 mt-1">
                      {deal.application_counts?.applied > 0 && <span className="text-amber-600">{deal.application_counts.applied} pending</span>}
                      {deal.application_counts?.approved > 0 && <span className="text-emerald-600">{deal.application_counts.approved} approved</span>}
                      {deal.application_counts?.accepted > 0 && <span className="text-green-600">{deal.application_counts.accepted} accepted</span>}
                    </div>
                  </div>
                </div>

                {deal.multi_partner && (
                  <DealPartnersPanel deal={deal} onChanged={loadApplications} />
                )}

                {/* Always-visible: view the promoted terms; contract action when committed */}
                <div className="mt-3 pt-3 border-t flex flex-wrap items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs h-7 rounded-sm gap-1.5"
                    onClick={() => setTermsDeal(deal)}
                    data-testid={`view-terms-${deal.id}`}
                  >
                    <Eye className="w-3 h-3" />
                    View Proposal Terms
                  </Button>
                  {deal.status === 'committed' && (
                    <>
                      <Button
                        variant="default"
                        size="sm"
                        className="text-xs h-7 rounded-sm gap-1.5 bg-emerald-700 hover:bg-emerald-800 text-white"
                        onClick={() => setContractDeal(deal)}
                        data-testid={`open-contract-${deal.id}`}
                      >
                        <FileText className="w-3 h-3" />
                        Open Contract
                      </Button>
                      <span className="text-[11px] text-muted-foreground ml-auto">
                        Edit & download the JV agreement, then upload to your e-signature provider.
                      </span>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>
      </Tabs>

      {/* Deal Contract Editor (manual flow) */}
      <DealContractEditor
        deal={contractDeal}
        open={!!contractDeal}
        onClose={() => setContractDeal(null)}
      />

      {/* Proposal Terms viewer — lets any owner see the promoted terms to draft the JV */}
      <ProposalTermsDialog
        deal={termsDeal}
        open={!!termsDeal}
        onClose={() => setTermsDeal(null)}
        onOpenContract={(d) => { setTermsDeal(null); setContractDeal(d); }}
      />

      {/* Promote Confirmation Dialog */}
      <Dialog open={!!confirmPromote} onOpenChange={() => setConfirmPromote(null)}>
        <DialogContent className="sm:max-w-sm" data-testid="promote-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Promote to Active Partner</DialogTitle>
          </DialogHeader>
          {confirmPromote && (
            <div className="py-2 space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#1A2F23] flex items-center justify-center text-white font-bold text-sm">
                  {confirmPromote.name?.charAt(0)?.toUpperCase()}
                </div>
                <div>
                  <div className="font-semibold text-sm">{confirmPromote.name}</div>
                  <div className="text-xs text-muted-foreground">{confirmPromote.email}</div>
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                This will create a portal login for <strong>{confirmPromote.name}</strong> and send them a welcome email with their credentials.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmPromote(null)} className="rounded-sm">Cancel</Button>
            <Button
              onClick={() => handlePromote(confirmPromote)}
              disabled={promoting === confirmPromote?.id}
              className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-sm gap-1.5"
              data-testid="promote-confirm-btn"
            >
              <Shield className="w-3.5 h-3.5" />
              {promoting === confirmPromote?.id ? 'Promoting...' : 'Promote to Active'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Partner Create/Edit Dialog ────────────────────────────────
function PartnerFormDialog({ open, onOpenChange, partner, onSaved }) {
  const [form, setForm] = useState({ name: '', entity_name: '', email: '', phone: '', total_available_capital: 0, notes: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (partner) {
      setForm({
        name: partner.name || '',
        entity_name: partner.entity_name || '',
        email: partner.email || '',
        phone: partner.phone || '',
        total_available_capital: partner.total_available_capital || 0,
        notes: partner.notes || '',
      });
    } else {
      setForm({ name: '', entity_name: '', email: '', phone: '', total_available_capital: 0, notes: '' });
    }
  }, [partner, open]);

  const handleSave = async () => {
    if (!form.name || !form.email) {
      toast.error('Name and email are required');
      return;
    }
    setSaving(true);
    try {
      if (partner) {
        await partnersApi.update(partner.id, form);
        toast.success('Partner updated');
      } else {
        await partnersApi.create(form);
        toast.success('Partner created');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save partner');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="partner-form-dialog">
        <DialogHeader>
          <DialogTitle>{partner ? 'Edit Partner' : 'Add New Partner'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Name *</Label>
              <Input value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} className="h-9 rounded-sm" data-testid="partner-form-name" />
            </div>
            <div>
              <Label className="text-xs">Entity Name</Label>
              <Input value={form.entity_name} onChange={(e) => setForm(f => ({ ...f, entity_name: e.target.value }))} className="h-9 rounded-sm" data-testid="partner-form-entity" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Email *</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} className="h-9 rounded-sm" data-testid="partner-form-email" />
            </div>
            <div>
              <Label className="text-xs">Phone</Label>
              <Input value={form.phone} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))} className="h-9 rounded-sm" data-testid="partner-form-phone" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Total Available Capital</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
              <Input
                type="text"
                value={form.total_available_capital === 0 ? '' : form.total_available_capital.toLocaleString('en-US')}
                onChange={(e) => setForm(f => ({ ...f, total_available_capital: parseFloat(e.target.value.replace(/[,$]/g, '')) || 0 }))}
                className="pl-7 h-9 rounded-sm font-mono"
                data-testid="partner-form-capital"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">Notes</Label>
            <Input value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} className="h-9 rounded-sm" data-testid="partner-form-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#1A2F23] hover:bg-[#1A2F23]/90 text-white rounded-sm" data-testid="partner-form-save">
            {saving ? 'Saving...' : (partner ? 'Update' : 'Create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Partner Detail Dialog ─────────────────────────────────────
function PartnerDetailDialog({ partner, onClose, onPromote, promoting, onRefresh }) {
  const [uploading, setUploading] = useState(false);
  const [showInvestorView, setShowInvestorView] = useState(false);
  const [investorData, setInvestorData] = useState(null);
  const [investorLoading, setInvestorLoading] = useState(false);

  if (!partner) return null;

  const handleViewAsInvestor = async () => {
    setInvestorLoading(true);
    try {
      const res = await investorApi.adminViewPortal(partner.id);
      setInvestorData(res.data);
      setShowInvestorView(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load investor view');
    } finally {
      setInvestorLoading(false);
    }
  };

  const handleUploadPOF = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await partnersApi.uploadPOF(partner.id, file);
      toast.success('Proof of Funds uploaded');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadPOF = async () => {
    try {
      const res = await partnersApi.downloadPOF(partner.id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = partner.proof_of_funds_filename || 'proof_of_funds.pdf';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Download failed');
    }
  };

  return (
    <Dialog open={!!partner} onOpenChange={() => onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="partner-detail-dialog">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#1A2F23] flex items-center justify-center text-white font-bold text-sm">
              {partner.name?.charAt(0)?.toUpperCase()}
            </div>
            <div>
              <DialogTitle className="text-lg">{partner.name}</DialogTitle>
              {partner.entity_name && <p className="text-xs text-muted-foreground">{partner.entity_name}</p>}
            </div>
            <Badge variant={partner.status === 'active' ? 'default' : 'secondary'} className={`ml-auto text-xs ${partner.status === 'active' ? 'bg-emerald-100 text-emerald-800' : ''}`}>
              {partner.status === 'active' ? 'Active Partner' : 'Prospect'}
            </Badge>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Contact Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center gap-2 text-sm">
              <Mail className="w-4 h-4 text-muted-foreground" />
              <span>{partner.email}</span>
            </div>
            {partner.phone && (
              <div className="flex items-center gap-2 text-sm">
                <Phone className="w-4 h-4 text-muted-foreground" />
                <span>{partner.phone}</span>
              </div>
            )}
          </div>

          <Separator />

          {/* Capital & POF */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Available Capital</div>
              <div className="text-lg font-bold font-mono">{fmt(partner.total_available_capital)}</div>
            </div>
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Proof of Funds</div>
              {partner.proof_of_funds_url ? (
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-emerald-600" />
                  <button onClick={handleDownloadPOF} className="text-sm text-blue-600 hover:underline" data-testid="download-pof-btn">
                    {partner.proof_of_funds_filename || 'Download'}
                  </button>
                </div>
              ) : (
                <label className="cursor-pointer inline-flex items-center gap-1.5 text-sm text-blue-600 hover:underline">
                  <Upload className="w-3.5 h-3.5" />
                  {uploading ? 'Uploading...' : 'Upload POF'}
                  <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.docx" onChange={handleUploadPOF} data-testid="upload-pof-input" />
                </label>
              )}
            </div>
          </div>

          {partner.notes && (
            <>
              <Separator />
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Notes</div>
                <p className="text-sm text-muted-foreground">{partner.notes}</p>
              </div>
            </>
          )}

          <Separator />

          {/* Promote Action */}
          {partner.status === 'prospect' && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold text-emerald-800">Promote to Active Partner</div>
                  <div className="text-xs text-emerald-700">Creates a portal login and sends a welcome email</div>
                </div>
                <Button
                  onClick={() => onPromote(partner)}
                  disabled={promoting === partner.id}
                  className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-sm gap-1.5"
                  data-testid="promote-detail-btn"
                >
                  <Shield className="w-3.5 h-3.5" />
                  {promoting === partner.id ? 'Promoting...' : 'Promote'}
                </Button>
              </div>
            </div>
          )}

          {partner.status === 'active' && (
            <div className="bg-muted/30 rounded-sm p-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <UserCheck className="w-4 h-4 text-emerald-600" />
                <span className="text-sm">Active portal login ({partner.email})</span>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs gap-1.5 rounded-sm"
                onClick={handleViewAsInvestor}
                disabled={investorLoading}
                data-testid="view-as-investor-btn"
              >
                {investorLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Eye className="w-3 h-3" />}
                {investorLoading ? 'Loading...' : 'View Portal'}
              </Button>
            </div>
          )}
        </div>

        {/* Admin View as Investor Modal */}
        <InvestorViewModal
          open={showInvestorView}
          onClose={() => setShowInvestorView(false)}
          data={investorData}
          partnerName={partner.name}
        />
      </DialogContent>
    </Dialog>
  );
}

// ── Admin: View as Investor Modal ─────────────────────────────
function InvestorViewModal({ open, onClose, data, partnerName }) {
  if (!data) return null;

  const p = data.partner || {};
  const stats = data.stats || {};
  const jvAgreements = data.jv_agreements || [];
  const deals = data.available_deals || [];

  const fmt = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(v || 0);

  const dealStatusBadge = (status) => {
    const map = {
      available: { label: 'Available', cls: 'bg-amber-100 text-amber-800' },
      applied: { label: 'Applied', cls: 'bg-blue-100 text-blue-800' },
      approved: { label: 'Approved', cls: 'bg-emerald-100 text-emerald-800' },
      accepted: { label: 'Accepted', cls: 'bg-green-100 text-green-800' },
      declined_by_partner: { label: 'Declined', cls: 'bg-gray-100 text-gray-600' },
      declined_by_admin: { label: 'Not Selected', cls: 'bg-red-100 text-red-700' },
      contract_sent: { label: 'Contract Sent', cls: 'bg-purple-100 text-purple-800' },
    };
    const m = map[status] || { label: status || 'Unknown', cls: 'bg-gray-100 text-gray-600' };
    return <Badge className={`text-[10px] ${m.cls}`}>{m.label}</Badge>;
  };

  const jvStatusBadge = (status) => {
    const map = {
      draft: { label: 'Draft', cls: 'bg-gray-100 text-gray-600' },
      sent: { label: 'Sent', cls: 'bg-blue-100 text-blue-800' },
      signed: { label: 'Signed', cls: 'bg-green-100 text-green-800' },
      declined: { label: 'Declined', cls: 'bg-red-100 text-red-700' },
    };
    const m = map[status] || { label: status || 'Pending', cls: 'bg-gray-100 text-gray-600' };
    return <Badge className={`text-[10px] ${m.cls}`}>{m.label}</Badge>;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="investor-view-modal">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Eye className="w-5 h-5 text-muted-foreground" />
            <DialogTitle className="text-lg">Investor Portal — {partnerName}</DialogTitle>
          </div>
          <p className="text-xs text-muted-foreground">Admin read-only view of what this investor sees in their portal</p>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Partner Info */}
          <div className="bg-muted/30 rounded-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold">{p.name}</div>
                <div className="text-xs text-muted-foreground">{p.entity_name || p.email}</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase text-muted-foreground tracking-wide">Available Capital</div>
                <div className="text-base font-bold font-mono">{fmt(p.total_available_capital)}</div>
              </div>
            </div>
            <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {p.email}</span>
              {p.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {p.phone}</span>}
              <span className="flex items-center gap-1">
                <FileText className="w-3 h-3" />
                POF: {p.has_proof_of_funds ? (
                  <span className="text-emerald-600 font-medium">Uploaded</span>
                ) : (
                  <span className="text-amber-600 font-medium">Missing</span>
                )}
              </span>
            </div>
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-muted/20 border rounded-sm p-3 text-center">
              <div className="text-lg font-bold">{fmt(stats.total_invested)}</div>
              <div className="text-[10px] uppercase text-muted-foreground tracking-wide">Total Invested</div>
            </div>
            <div className="bg-muted/20 border rounded-sm p-3 text-center">
              <div className="text-lg font-bold">{stats.active_investments || 0}</div>
              <div className="text-[10px] uppercase text-muted-foreground tracking-wide">Active JVs</div>
            </div>
            <div className="bg-muted/20 border rounded-sm p-3 text-center">
              <div className="text-lg font-bold">{stats.signed_agreements || 0}</div>
              <div className="text-[10px] uppercase text-muted-foreground tracking-wide">Signed</div>
            </div>
          </div>

          <Separator />

          {/* JV Agreements */}
          <div>
            <h4 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
              <Handshake className="w-4 h-4" /> JV Agreements ({jvAgreements.length})
            </h4>
            {jvAgreements.length === 0 ? (
              <p className="text-xs text-muted-foreground bg-muted/20 rounded-sm p-3">No JV agreements for this partner.</p>
            ) : (
              <div className="space-y-2">
                {jvAgreements.map(jv => (
                  <div key={jv.id} className="border rounded-sm p-3" data-testid={`jv-view-${jv.id}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium">{jv.project_name || jv.investor_name || 'JV Agreement'}</div>
                        <div className="text-xs text-muted-foreground font-mono">
                          Capital: {fmt(jv.capital_contributed || jv.capital_amount)}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {jvStatusBadge(jv.signing_status)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <Separator />

          {/* Available / Promoted Deals */}
          <div>
            <h4 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
              <Send className="w-4 h-4" /> Deal Flow ({deals.length})
            </h4>
            {!p.has_proof_of_funds && (
              <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 mb-3">
                <div className="flex items-center gap-2 text-amber-800 text-xs">
                  <Shield className="w-3.5 h-3.5 flex-shrink-0" />
                  <span>This investor has <strong>not uploaded Proof of Funds</strong>. They cannot see available deals until POF is uploaded.</span>
                </div>
              </div>
            )}
            {deals.length === 0 ? (
              <p className="text-xs text-muted-foreground bg-muted/20 rounded-sm p-3">No promoted deals for this partner.</p>
            ) : (
              <div className="space-y-2">
                {deals.map(deal => {
                  const s = deal.summary || {};
                  return (
                    <div key={deal.id} className="border rounded-sm p-3" data-testid={`deal-view-${deal.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium">{s.project_name || s.proposal_name || 'Deal'}</span>
                            {dealStatusBadge(deal.my_status)}
                          </div>
                          <div className="flex gap-3 text-xs text-muted-foreground mt-1">
                            <span>Purchase: {fmt(s.purchase_price)}</span>
                            <span>Capital: {fmt(s.total_investor_capital)}</span>
                            {s.projected_roi && <span>ROI: {s.projected_roi?.toFixed(1)}%</span>}
                          </div>
                        </div>
                      </div>
                      {deal.application && (
                        <div className="mt-2 bg-muted/30 rounded-sm px-2 py-1.5 text-[11px] text-muted-foreground">
                          Applied: {deal.application.applied_at ? new Date(deal.application.applied_at).toLocaleDateString() : '—'}
                          {deal.application.status === 'declined_by_partner' && deal.application.decline_reason && (
                            <span className="ml-2">— Reason: {deal.application.decline_reason}</span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default Partners;


// ── Manual Contract Drafting Dialog ──────────────────────────────────────


// ── Multi-partner roster + capital subscription panel (Phase 2) ──────────
function DealPartnersPanel({ deal, onChanged }) {
  const [apps, setApps] = useState(null);
  const [busy, setBusy] = useState(false);
  const [edits, setEdits] = useState({});
  const target = deal.investor_capital_target || deal.summary?.total_investor_capital || 0;

  const load = useCallback(async () => {
    try {
      const res = await dealFlowApi.getPromoted(deal.id);
      setApps(res.data?.applications || []);
    } catch {
      setApps([]);
    }
  }, [deal.id]);

  useEffect(() => { load(); }, [load]);

  if (apps === null) {
    return <div className="mt-3 pt-3 border-t text-xs text-muted-foreground">Loading partners…</div>;
  }

  const accepted = apps.filter(a => a.status === 'accepted');
  const pending = apps.filter(a => a.status === 'accepted_pending_confirmation');
  const committed = accepted.reduce((s, a) => s + (Number(a.committed_capital) || 0), 0);
  const remaining = Math.max(0, target - committed);
  const pct = target > 0 ? Math.min(100, (committed / target) * 100) : 0;

  const confirm = async (app) => {
    const raw = edits[app.id] ?? app.proposed_capital ?? '';
    setBusy(true);
    try {
      await dealFlowApi.confirmAllocation(deal.id, app.id, {
        committed_capital: Number(String(raw).replace(/[,$]/g, '')) || 0,
      });
      toast.success(`Confirmed ${app.partner_name}'s allocation`);
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to confirm');
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    if (!window.confirm('Commit this deal now? Partners still deciding will be declined.')) return;
    setBusy(true);
    try {
      await dealFlowApi.commitDeal(deal.id);
      toast.success('Deal committed');
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to commit');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 pt-3 border-t" data-testid={`deal-partners-panel-${deal.id}`}>
      <div className="flex items-center justify-between text-[11px] mb-1">
        <span className="font-medium uppercase tracking-wide text-muted-foreground">Capital subscription</span>
        <span className="font-mono" data-testid={`deal-fill-${deal.id}`}>
          {fmt(committed)} / {fmt(target)}{remaining > 0 && deal.status === 'open' ? ` · ${fmt(remaining)} open` : ''}
        </span>
      </div>
      <div className="h-2 rounded-full bg-muted overflow-hidden">
        <div className="h-full bg-emerald-600 transition-all" style={{ width: `${pct}%` }} />
      </div>

      <div className="mt-2 space-y-1">
        {accepted.map(a => {
          const share = committed > 0 ? (Number(a.committed_capital) || 0) / committed * 100 : 0;
          return (
            <div key={a.id} className="flex items-center gap-2 text-xs" data-testid={`deal-partner-${a.id}`}>
              <CheckCircle2 className="w-3 h-3 text-emerald-600 flex-shrink-0" />
              <span className="flex-1 truncate">{a.partner_name}</span>
              <span className="font-mono">{fmt(a.committed_capital)}</span>
              <span className="text-muted-foreground w-12 text-right">{share.toFixed(1)}%</span>
            </div>
          );
        })}
        {accepted.length === 0 && pending.length === 0 && (
          <div className="text-[11px] text-muted-foreground">No partners have accepted yet.</div>
        )}
      </div>

      {pending.length > 0 && (
        <div className="mt-2 space-y-1.5">
          <div className="text-[11px] font-medium text-amber-700">Awaiting your confirmation</div>
          {pending.map(a => (
            <div key={a.id} className="flex items-center gap-2" data-testid={`pending-app-${a.id}`}>
              <Clock className="w-3 h-3 text-amber-600 flex-shrink-0" />
              <span className="flex-1 truncate text-xs">{a.partner_name}</span>
              <span className="text-[10px] text-muted-foreground">proposed</span>
              <input
                type="number"
                min="0"
                value={edits[a.id] ?? (a.proposed_capital ?? '')}
                onChange={e => setEdits(p => ({ ...p, [a.id]: e.target.value }))}
                className="h-7 w-28 border rounded-sm px-2 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
                data-testid={`confirm-amount-${a.id}`}
              />
              <Button
                size="sm"
                className="h-7 text-xs rounded-sm bg-[#C68E5D] hover:bg-[#C68E5D]/90 text-white"
                disabled={busy}
                onClick={() => confirm(a)}
                data-testid={`confirm-allocation-${a.id}`}
              >
                Confirm
              </Button>
            </div>
          ))}
        </div>
      )}

      {deal.status === 'open' && accepted.length > 0 && (
        <div className="mt-2 flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs rounded-sm"
            disabled={busy}
            onClick={commit}
            data-testid={`commit-deal-${deal.id}`}
          >
            Commit deal now
          </Button>
          <span className="text-[10px] text-muted-foreground">Auto-commits when the capital target is filled.</span>
        </div>
      )}
    </div>
  );
}




// ── Proposal Terms viewer ────────────────────────────────────────────────
// Read-only view of the promoted proposal so any owner (not just the one who
// sent it) can see the exact terms — economics, funding split and the
// profit-split waterfall — to draft a JV agreement that aligns with it.

function TermsRow({ label, value, strong }) {
  return (
    <div className="flex justify-between gap-3 text-xs py-1.5 border-b border-border/40 last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className={strong ? 'font-semibold text-foreground' : 'font-medium text-foreground'}>{value}</span>
    </div>
  );
}

function TermsSplitRow({ label, lf }) {
  const lfPct = lf || 0;
  const invPct = 100 - lfPct;
  return (
    <div className="flex justify-between gap-3 text-xs py-1.5 border-b border-border/40 last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-foreground">
        LF {lfPct}% · <span className="font-semibold text-emerald-700">Investor {invPct}%</span>
      </span>
    </div>
  );
}

function ProposalTermsDialog({ deal, open, onClose, onOpenContract }) {
  const [downloadingAtt, setDownloadingAtt] = useState(null);
  if (!deal) return null;

  const s = deal.summary || {};
  const fd = deal.full_details || {};
  const d = fd.deal || {};
  const f = fd.funding || {};
  const scenario = fd.selected_scenario || 'A';
  const tiers = (scenario === 'B' ? fd.option_b_tiers : fd.option_a_tiers) || [];
  const totalDD = (d.survey || 0) + (d.soil || 0) + (d.otherDD || 0);
  const saleCommissionAmt = (d.estimatedSalePrice || 0) * ((d.saleCommission || 0) / 100);
  const attachments = deal.attachments || [];

  const handleDownloadAtt = async (att) => {
    setDownloadingAtt(att.id);
    try {
      const res = await dealFlowApi.downloadAttachment(deal.id, att.id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: att.content_type || 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = att.filename || att.name || 'attachment.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Attachment download failed:', e?.message);
      toast.error('Failed to download attachment');
    } finally {
      setDownloadingAtt(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="proposal-terms-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Eye className="w-4 h-4" /> Proposal Terms
          </DialogTitle>
          <DialogDescription className="sr-only">
            Read-only view of the promoted proposal terms — economics, funding split and profit-split waterfall.
          </DialogDescription>
        </DialogHeader>

        {/* Header info */}
        <div className="rounded-sm border bg-muted/30 p-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm" data-testid="terms-project-name">
              {s.project_name || s.proposal_name || 'Proposal'}
            </span>
            <Badge variant="outline" className="text-[10px] capitalize">{deal.status}</Badge>
            {deal.deal_type && <Badge variant="outline" className="text-[10px] uppercase">{deal.deal_type}</Badge>}
          </div>
          {s.proposal_name && s.proposal_name !== s.project_name && (
            <div className="text-xs text-muted-foreground mt-0.5">{s.proposal_name}</div>
          )}
          <div className="text-[11px] text-muted-foreground mt-1.5">
            {deal.created_by && <>Promoted by <span className="font-medium text-foreground/70">{deal.created_by}</span></>}
            {deal.created_at && <> · {new Date(deal.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</>}
            {' · '}{deal.partner_ids?.length || 0} partner{deal.partner_ids?.length !== 1 ? 's' : ''} invited
          </div>
        </div>

        {/* Deal Economics */}
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1 flex items-center gap-1.5">
            <DollarSign className="w-3.5 h-3.5" /> Deal Economics
          </h4>
          <div className="rounded-sm border px-3 py-1" data-testid="terms-economics">
            <TermsRow label="Purchase Price" value={fmt(d.purchasePrice ?? s.purchase_price)} />
            <TermsRow label="Due Diligence (Survey + Soil + Other)" value={fmt(totalDD)} />
            <TermsRow label="Purchase Closing Costs" value={fmt(d.purchaseClosingCosts)} />
            <TermsRow label="Improvements" value={fmt(d.improvements)} />
            <TermsRow label="Estimated Sale Price" value={fmt(d.estimatedSalePrice ?? s.estimated_sale_price)} />
            <TermsRow label={`Sale Commission (${d.saleCommission || 0}%)`} value={fmt(saleCommissionAmt)} />
            <TermsRow label="Sale Closing Costs" value={fmt(d.saleClosingCosts)} />
            <TermsRow label="Projected Gross Profit" value={fmt(s.gross_profit)} strong />
          </div>
        </div>

        {/* Funding split */}
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1 flex items-center gap-1.5">
            <Percent className="w-3.5 h-3.5" /> Funding Split (Land Finder vs Investor)
          </h4>
          <div className="rounded-sm border px-3 py-1" data-testid="terms-funding">
            <TermsSplitRow label="Purchase Price" lf={f.lfPurchasePrice} />
            <TermsSplitRow label="Due Diligence" lf={f.lfDDCosts} />
            <TermsSplitRow label="Improvements" lf={f.lfImprovements} />
            <TermsSplitRow label="Closing Costs" lf={f.lfClosingCosts} />
            <TermsRow label="Total Investor Capital" value={fmt(s.total_investor_capital)} strong />
          </div>
        </div>

        {/* Profit-split waterfall */}
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1 flex items-center gap-1.5">
            <TrendingUp className="w-3.5 h-3.5" /> Profit-Split Waterfall — Scenario {scenario}
          </h4>
          {tiers.length === 0 ? (
            <p className="text-xs text-muted-foreground rounded-sm border px-3 py-2">No tier schedule recorded for this proposal.</p>
          ) : (
            <div className="rounded-sm border overflow-hidden" data-testid="terms-waterfall">
              <table className="w-full text-xs">
                <thead className="bg-muted/40">
                  <tr className="text-left text-[10px] uppercase tracking-wide text-muted-foreground">
                    <th className="px-3 py-1.5">Hold Window</th>
                    <th className="px-3 py-1.5 text-right">Investor Split</th>
                    <th className="px-3 py-1.5 text-right">Land Finder Split</th>
                  </tr>
                </thead>
                <tbody>
                  {tiers.map((t, i) => (
                    <tr key={i} className="border-t">
                      <td className="px-3 py-1.5">Day {t.startDay ?? '—'}{t.endDay ? `–${t.endDay}` : '+'}</td>
                      <td className="px-3 py-1.5 text-right font-semibold text-emerald-700">{t.investorSplit ?? 0}%</td>
                      <td className="px-3 py-1.5 text-right">{100 - (t.investorSplit ?? 0)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="text-[11px] text-muted-foreground mt-1">
            Projected investor ROI: <span className="font-medium text-foreground">{(s.projected_roi ?? 0).toFixed(1)}%</span>
          </p>
        </div>

        {/* Notes / video / attachments */}
        {(s.description || deal.admin_notes || deal.video_url || attachments.length > 0) && (
          <div className="space-y-2">
            {s.description && (
              <div>
                <div className="text-xs font-semibold text-muted-foreground mb-0.5">Description</div>
                <p className="text-xs whitespace-pre-wrap">{s.description}</p>
              </div>
            )}
            {deal.admin_notes && (
              <div>
                <div className="text-xs font-semibold text-muted-foreground mb-0.5">Internal Notes</div>
                <p className="text-xs whitespace-pre-wrap">{deal.admin_notes}</p>
              </div>
            )}
            {deal.video_url && (
              <a href={deal.video_url} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline inline-flex items-center gap-1">
                <Link2 className="w-3 h-3" /> Proposal video
              </a>
            )}
            {attachments.length > 0 && (
              <div>
                <div className="text-xs font-semibold text-muted-foreground mb-1">Attachments</div>
                <div className="space-y-1">
                  {attachments.map((att) => (
                    <button
                      key={att.id}
                      type="button"
                      onClick={() => handleDownloadAtt(att)}
                      disabled={downloadingAtt === att.id}
                      className="flex items-center gap-1.5 text-xs text-primary hover:underline disabled:opacity-50"
                      data-testid={`terms-attachment-${att.id}`}
                    >
                      {downloadingAtt === att.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                      {att.filename || att.name || 'Attachment'}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} data-testid="terms-close-btn">Close</Button>
          {deal.status === 'committed' && onOpenContract && (
            <Button
              size="sm"
              className="bg-emerald-700 hover:bg-emerald-800 text-white"
              onClick={() => onOpenContract(deal)}
              data-testid="terms-open-contract-btn"
            >
              <FileText className="w-3 h-3 mr-1" /> Draft JV Agreement
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}